import React, { useState } from 'react';
import { bool, func, string } from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { IconSpinner, InlineTextButton } from '..';
import { INSURANCE_STATUSES } from '../../util/user';
import PricingDetailsCard from '../PricingDetails/PricingDetailsCard';
import { ReactComponent as ShieldCheckIcon } from '../../assets/icons/shield-check-regular.svg';
import css from './LandownerInsurance.module.css';

const CoverageSummary = ({
  enrollmentSuccess,
  formattedCoverageEndDate,
  formattedCoveragePrice,
  formattedCoverageStartDate,
  hasPaymentIssue,
  insuranceStatus,
  isRefreshingStatus,
  onShowPaymentMethodUpdate,
  paymentMethodLabel,
}) => {
  const [isBreakdownOpen, setIsBreakdownOpen] = useState(true);
  const isEnrolled =
    enrollmentSuccess ||
    isRefreshingStatus ||
    hasPaymentIssue ||
    [INSURANCE_STATUSES.ACTIVE, INSURANCE_STATUSES.PENDING].includes(insuranceStatus);
  const showPaymentIssueCopy = hasPaymentIssue && !isRefreshingStatus;
  const showPendingCopy =
    !showPaymentIssueCopy && (isRefreshingStatus || insuranceStatus === INSURANCE_STATUSES.PENDING);
  let descriptionMessageId = 'LandownerInsurance.enrolledDescription';

  if (showPaymentIssueCopy) {
    descriptionMessageId = 'LandownerInsurance.paymentIssueDescription';
  } else if (showPendingCopy) {
    descriptionMessageId = 'LandownerInsurance.enrolledPendingDescription';
  }

  if (!isEnrolled) {
    return null;
  }

  return (
    <div className={css.enrolledWindow}>
      <div className={css.enrolledHeader}>
        <span className={css.enrolledIcon} aria-hidden="true">
          <ShieldCheckIcon />
        </span>
        <div className={css.sectionIntro}>
          <h3 className={css.sectionTitle}>
            <FormattedMessage id="LandownerInsurance.enrolledHeading" />
          </h3>
          <div className={css.supportText}>
            <FormattedMessage id={descriptionMessageId} />
          </div>
        </div>
        {isRefreshingStatus ? (
          <div className={css.inlineLoader}>
            <IconSpinner className={css.inlineSpinnerIcon} />
          </div>
        ) : null}
      </div>

      <PricingDetailsCard
        className={css.summaryCard}
        isExpanded={isBreakdownOpen}
        onToggle={() => setIsBreakdownOpen(!isBreakdownOpen)}
        ariaLabel="Toggle insurance price breakdown"
        summary={
          <div className={css.summaryPlanHeader}>
            <div className={css.summaryPrice}>
              <FormattedMessage
                id="LandownerInsurance.summaryPrice"
                values={{ price: formattedCoveragePrice }}
              />
            </div>
            <div className={css.summarySubtext}>
              <FormattedMessage id="LandownerInsurance.summaryCoveredListings" />
            </div>
          </div>
        }
      >
        <div className={css.summaryRows}>
          <div className={css.summaryRow}>
            <span>
              <FormattedMessage id="LandownerInsurance.summaryPlan" />
            </span>
            <span>
              <FormattedMessage
                id="LandownerInsurance.summaryPlanValue"
                values={{ price: formattedCoveragePrice }}
              />
            </span>
          </div>
          {formattedCoverageStartDate ? (
            <div className={css.summaryRow}>
              <span>
                <FormattedMessage id="LandownerInsurance.summaryEnrolledOn" />
              </span>
              <span>{formattedCoverageStartDate}</span>
            </div>
          ) : null}
          {formattedCoverageEndDate ? (
            <div className={css.summaryRow}>
              <span>
                <FormattedMessage id="LandownerInsurance.summaryNextRenewal" />
              </span>
              <span>{formattedCoverageEndDate}</span>
            </div>
          ) : null}
          {paymentMethodLabel ? (
            <div className={css.summaryRow}>
              <span>
                <FormattedMessage id="LandownerInsurance.summaryPaymentMethod" />
              </span>
              <span>{paymentMethodLabel}</span>
            </div>
          ) : null}
        </div>
      </PricingDetailsCard>

      {hasPaymentIssue ? (
        <div className={css.paymentIssueAlert}>
          <FormattedMessage id="LandownerInsurance.paymentIssueAlert" />
        </div>
      ) : null}

      <div className={css.updatePaymentLinkWrapper}>
        <InlineTextButton
          type="button"
          className={css.updatePaymentLink}
          onClick={onShowPaymentMethodUpdate}
        >
          <FormattedMessage id="LandownerInsurance.updatePaymentMethod" />
        </InlineTextButton>
      </div>
    </div>
  );
};

CoverageSummary.propTypes = {
  enrollmentSuccess: bool.isRequired,
  formattedCoverageEndDate: string,
  formattedCoveragePrice: string.isRequired,
  formattedCoverageStartDate: string,
  hasPaymentIssue: bool,
  insuranceStatus: string,
  isRefreshingStatus: bool.isRequired,
  onShowPaymentMethodUpdate: func.isRequired,
  paymentMethodLabel: string,
};

CoverageSummary.defaultProps = {
  formattedCoverageEndDate: null,
  formattedCoverageStartDate: null,
  hasPaymentIssue: false,
  insuranceStatus: null,
  paymentMethodLabel: null,
};

export default CoverageSummary;
